import { disableCustomerUsersApi, useCustomerUserCount, useCustomerUserList } from "@/api/services/main/customer-user";
import { TCustomerUserListItemDto } from "@/api/services/main/customer-user/dtos/customer-user.dto";
import { IconImport } from "@/assets/icons/IconImport";
import { Button } from "@/components/elements/Button";
import { EllipsisText } from "@/components/elements/EllipsisText";
import { Modal } from "@/components/elements/Modal";
import { Pagination } from "@/components/elements/Pagination";
import { Table } from "@/components/elements/Table";
import { UserStatusBadge } from "@/features/customer-user/components/UserStatusBadge";
import { accessibleProductCodes } from "@/features/customer-user/utils/productAccess";
import { CustomerUserImportModal } from "@/features/customer-user/components/import/CustomerUserImportModal";
import {
  getPICGuardOffenders,
  TCannotDeactivateUser,
  UserCannotDeactivateModal,
} from "@/features/customer-user/components/UserCannotDeactivateModal";
import { useMe } from "@/api/services/main/auth";
import { useCurrentUserRole } from "@/features/auth/hooks/useCurrentUserRole";
import { useDialog } from "@/hooks/useDialog";
import { useVisible } from "@/hooks/useVisible";
import { usePagination } from "@/hooks/usePagination";
import { useTable } from "@/hooks/useTable";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { EMPTY_STR } from "@/constants/string";
import { customerUserPath, AppRoutes } from "@/routes/config";
import { text } from "@/theme/colors";
import { USER_ROLE_LABEL, USER_STATUS, TUserRole } from "@/types/enum";
import { formatDateOrDash } from "@/utils/dayjs";
import { isApiError, showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { MRT_ColumnDef, MRT_RowSelectionState } from "material-react-table";
import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconNavigationPlus } from "@/assets/icons";
import { IconContentArchive } from "@/assets/icons/IconContentArchive";

const roleLabel = (role: string) => USER_ROLE_LABEL[role as TUserRole] ?? role;

export const CustomerUserListTable: FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const query = usePagination();
  const { data: users, pagination, isLoading, isFetching } = useCustomerUserList(query);
  const { count: activeCount } = useCustomerUserCount();
  const { canMutateUsers } = useCurrentUserRole();
  const { data: me } = useMe();
  const currentUserId = me?.data?.id;

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const confirmDialog = useDialog();
  const importDialog = useDialog();
  const cannotDeactivateModal = useVisible();
  const [usersCannotDeactivate, setUsersCannotDeactivate] = useState<TCannotDeactivateUser[]>([]);
  const [selectedCountCannotDeactivate, setSelectedCountCannotDeactivate] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");

  const selectedIds = useMemo(
    () =>
      Object.keys(rowSelection)
        .filter((id) => rowSelection[id])
        .map(Number),
    [rowSelection],
  );

  const columns = useMemo<MRT_ColumnDef<TCustomerUserListItemDto>[]>(
    () => [
      {
        header: "ステータス",
        id: "status",
        accessorKey: "status",
        size: 108,
        Cell: ({ row }) => <UserStatusBadge status={row.original.status} />,
      },
      {
        header: "ユーザー名",
        id: "fullName",
        accessorKey: "fullName",
        size: 180,
        Cell: ({ row }) => <EllipsisText title={row.original.fullName} variant="body14" color="inherit" maxWidth={164} />,
      },
      {
        header: "メールアドレス",
        id: "email",
        accessorKey: "email",
        size: 271,
        Cell: ({ row }) => <EllipsisText title={row.original.email} variant="body14" color="inherit" maxWidth={250} />,
      },
      { header: "権限", id: "role", accessorKey: "role", size: 74, Cell: ({ row }) => <>{roleLabel(row.original.role)}</> },
      {
        header: "プロダクトアクセス",
        id: "productAccess",
        size: 161,
        Cell: ({ row }) => {
          const codes = accessibleProductCodes(row.original.productAccess);
          if (codes.length) return <EllipsisText title={codes.join("、")} variant="body14" color="inherit" maxWidth={150} />;
          return (
            <Typography variant="body14" color="inherit" sx={{ ml: "-0.75em" }}>
              （アクセス権なし）
            </Typography>
          );
        },
      },
      {
        header: "最終ログイン",
        id: "lastLoginAt",
        accessorKey: "lastLoginAt",
        size: 116,
        Cell: ({ row }) => <>{row.original.lastLoginAt ? formatDateOrDash(row.original.lastLoginAt) : EMPTY_STR.DATE}</>,
      },
      {
        header: "登録日",
        id: "createdAt",
        accessorKey: "createdAt",
        size: 106,
        Cell: ({ row }) => <>{formatDateOrDash(row.original.createdAt)}</>,
      },
      {
        header: "",
        id: "actions",
        size: 92,
        Cell: ({ row }) => {
          // The caller's own row is listed/counted but has no detail page (self-edit lives in
          // ユーザー設定, and GET /users/:id rejects the caller), so hide 詳細 for it.
          if (row.original.id === currentUserId) return null;
          return (
            <Button
              variant="secondary"
              size="sm"
              sx={{ width: "60px", pointerEvents: "auto" }}
              onClick={() => navigate(customerUserPath.detail(row.original.id))}
            >
              詳細
            </Button>
          );
        },
      },
    ],
    [navigate, currentUserId],
  );

  const { table } = useTable<TCustomerUserListItemDto>({
    columns,
    rows: users,
    getRowId: (row) => String(row.id),
    disableRow: (row) => row.status === USER_STATUS.DISABLED,
    enableRowSelection: (row) => canMutateUsers && row.original.status === USER_STATUS.ACTIVE && row.original.id !== currentUserId,
    enableMultiRowSelection: true,
    state: { rowSelection },
    onRowSelectionChange: setRowSelection,
  });
  const handleConfirmDisable = async () => {
    setErrorMessage("");
    confirmDialog.confirm();
    try {
      await disableCustomerUsersApi({ ids: selectedIds });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOMER_USER.FETCH_LIST] }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOMER_USER.FETCH_COUNT] }),
      ]);
      setRowSelection({});
      toast.success("選択したユーザーを停止しました。");
    } catch (error) {
      if (isApiError(error)) {
        setErrorMessage(error.message);
      }
      // Users set as an organization's 担当者 can't be disabled — the API returns the
      // offenders in `error`; show them in a modal instead of a collapsed toast line.
      const offenders = getPICGuardOffenders(error);
      if (offenders) {
        setUsersCannotDeactivate(offenders);
        setSelectedCountCannotDeactivate(selectedIds.length);
        cannotDeactivateModal.open();
        return;
      }
      showError(error);
    }
  };

  // Header count is the active (使用中) user total, matching the dashboard ご利用ユーザー数; the table itself
  // still lists disabled users (sorted below) so they can be managed, so this is not the row count.
  const total = activeCount ?? pagination?.total ?? users.length;

  return (
    <Stack gap="16px">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="head20Bold" color={text.primary}>
          ユーザーの一覧 ({total})
        </Typography>
        {canMutateUsers && (
          <Stack direction="row" gap="12px">
            <Button variant="outline" size="sm" sx={{ width: "142px" }} onClick={() => importDialog.show()} startIcon={<IconImport />}>
              インポート
            </Button>
            <Button
              variant="primary"
              size="sm"
              sx={{ width: "200px" }}
              onClick={() => navigate(AppRoutes.customerUserCreate)}
              startIcon={<IconNavigationPlus />}
            >
              新しいユーザーを登録
            </Button>
          </Stack>
        )}
      </Stack>

      {/* Constrain the table so its body scrolls internally (like Match's list tables), keeping the
          pagination row below always in the viewport without scrolling the whole page. */}
      <Table table={table} isLoading={isLoading} isFetching={isFetching} maxHeight="calc(100vh - 360px)" />

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Pagination totalPage={pagination?.totalPage ?? 1} />
        {canMutateUsers && (
          <Button
            variant="danger"
            size="sm"
            sx={{ width: "214px" }}
            disabled={selectedIds.length === 0}
            onClick={() => confirmDialog.show()}
            startIcon={<IconContentArchive />}
          >
            選択したユーザーを停止
          </Button>
        )}
      </Stack>

      <Modal
        isOpen={confirmDialog.open}
        onClose={confirmDialog.cancel}
        onOk={handleConfirmDisable}
        labelClose="いいえ"
        labelOk="はい"
        width={500}
        title="停止の確認"
      >
        <Stack mb={2}>
          <Typography>選択した{selectedIds.length}名のユーザーを停止します。よろしいですか？</Typography>
        </Stack>
      </Modal>

      <CustomerUserImportModal isOpen={importDialog.open} onClose={importDialog.cancel} />

      <UserCannotDeactivateModal
        isOpen={cannotDeactivateModal.visible}
        onClose={cannotDeactivateModal.close}
        users={usersCannotDeactivate}
        selectedCount={selectedCountCannotDeactivate}
        errorMessage={errorMessage}
      />
    </Stack>
  );
};
